import React, { useState, useEffect, useCallback } from 'react';
import {
  Files, Loader2, Download, ChevronLeft, ChevronRight, FileText,
  Globe, User, CheckCircle2, FileEdit
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import api from '../services/api';
import { toast } from 'sonner';

const SOURCE_BADGES = {
  public: 'bg-blue-100 text-blue-800',
  internal: 'bg-green-100 text-green-800',
};

const STATUS_BADGES = {
  draft: 'bg-gray-100 text-gray-700',
  completed: 'bg-green-100 text-green-700',
  pending_verification: 'bg-amber-100 text-amber-800',
};

const AdminCreatedLOIs = () => {
  const [lois, setLois] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [sourceFilter, setSourceFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [downloading, setDownloading] = useState(null);
  const limit = 20;

  const fetchLois = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, limit });
      if (sourceFilter) params.append('source', sourceFilter);
      if (statusFilter) params.append('status', statusFilter);
      const res = await api.get(`/admin/loi-creation/all?${params}`);
      setLois(res.data.lois || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch (error) {
      toast.error('Failed to load created LOIs');
    } finally {
      setLoading(false);
    }
  }, [page, sourceFilter, statusFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/admin/loi-creation/stats');
      setStats(res.data);
    } catch (error) {
      // stats are non-critical; table still works without them
    }
  }, []);

  useEffect(() => {
    fetchLois();
  }, [fetchLois]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleDownload = async (loi) => {
    setDownloading(loi.id);
    try {
      const res = await api.get(`/loi-creation/${loi.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `LOI-${loi.loi_number || loi.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download LOI PDF');
    } finally {
      setDownloading(null);
    }
  };

  const companyName = (party, fallback) => party?.company_name || fallback || '—';
  const commodity = (loi) => loi.product?.commodity_type || loi.commodity || '—';
  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : '—');

  const statCards = stats ? [
    { label: 'Total LOIs', value: stats.total ?? 0, icon: FileText, color: '#2B4B6A' },
    { label: 'Public Wizard', value: stats.by_source?.public ?? stats.public_count ?? 0, icon: Globe, color: '#2563EB' },
    { label: 'Internal (Users)', value: stats.by_source?.internal ?? stats.internal_count ?? 0, icon: User, color: '#16A34A' },
    { label: 'Completed', value: stats.by_status?.completed ?? stats.completed_count ?? 0, icon: CheckCircle2, color: '#16A34A' },
    { label: 'Drafts', value: stats.by_status?.draft ?? stats.draft_count ?? 0, icon: FileEdit, color: '#6B7280' },
  ] : [];

  return (
    <Sidebar>
      <div className="p-6 md:p-8 bg-gray-50/50 min-h-screen">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Files className="w-6 h-6 text-[#2B4B6A]" /> Created LOIs via Trados
            </h1>
            <p className="text-gray-500 mt-1">
              Every LOI generated with the Trados LOI creator — public wizard and logged-in users.
            </p>
          </div>

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
              {statCards.map((s) => (
                <div key={s.label} className="bg-white border border-gray-200 rounded-xl p-4" data-testid={`stat-${s.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                  <s.icon className="w-4 h-4 mb-2" style={{ color: s.color }} />
                  <p className="text-2xl font-bold text-gray-900">{s.value}</p>
                  <p className="text-xs text-gray-500">{s.label}</p>
                </div>
              ))}
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <select
              value={sourceFilter}
              onChange={(e) => { setSourceFilter(e.target.value); setPage(1); }}
              data-testid="source-filter"
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
            >
              <option value="">All sources</option>
              <option value="public">Public wizard</option>
              <option value="internal">Internal (logged-in)</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              data-testid="status-filter"
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
            >
              <option value="">All statuses</option>
              <option value="draft">Draft</option>
              <option value="completed">Completed</option>
            </select>
            <span className="text-sm text-gray-500 ml-auto">{total} LOIs</span>
          </div>

          {/* Table */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            {loading ? (
              <div className="p-12 text-center">
                <Loader2 className="w-6 h-6 animate-spin inline text-gray-400" data-testid="lois-loading" />
              </div>
            ) : lois.length === 0 ? (
              <div className="p-12 text-center text-gray-500" data-testid="lois-empty">
                <Files className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                <p>No LOIs match these filters.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500 text-left">
                    <tr>
                      <th className="px-4 py-3 font-medium">LOI #</th>
                      <th className="px-4 py-3 font-medium">Buyer</th>
                      <th className="px-4 py-3 font-medium">Seller</th>
                      <th className="px-4 py-3 font-medium">Commodity</th>
                      <th className="px-4 py-3 font-medium">Source</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium">Created</th>
                      <th className="px-4 py-3 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lois.map((loi) => (
                      <tr key={loi.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`loi-row-${loi.id}`}>
                        <td className="px-4 py-3 font-mono text-xs text-gray-900">{loi.loi_number || '—'}</td>
                        <td className="px-4 py-3 text-gray-900">{companyName(loi.buyer, loi.buyer_name)}</td>
                        <td className="px-4 py-3 text-gray-600">{companyName(loi.seller, loi.seller_name)}</td>
                        <td className="px-4 py-3 text-gray-600">{commodity(loi)}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${SOURCE_BADGES[loi.source] || 'bg-gray-100 text-gray-700'}`}>
                            {loi.source || 'unknown'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGES[loi.status] || 'bg-gray-100 text-gray-700'}`}>
                            {loi.status || '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-500">{fmtDate(loi.created_at)}</td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => handleDownload(loi)}
                            disabled={downloading === loi.id}
                            data-testid={`download-btn-${loi.id}`}
                            title="Download PDF"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-lg text-xs text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                          >
                            {downloading === loi.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                            PDF
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-4">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                data-testid="prev-page-btn"
                className="p-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-100 disabled:opacity-40"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-600">Page {page} of {pages}</span>
              <button
                onClick={() => setPage((p) => Math.min(pages, p + 1))}
                disabled={page >= pages}
                data-testid="next-page-btn"
                className="p-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-100 disabled:opacity-40"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </Sidebar>
  );
};

export default AdminCreatedLOIs;
